export class WheelManager {
  private _options: string[] = [];

  constructor(initialOptions: string[] = []) {
    this._options = [...initialOptions];
  }

  get options(): string[] {
    return this._options;
  }

  /**
   * Add a new option to the wheel.
   */
  addOption(option: string): void {
    const trimmed = option.trim();
    if (trimmed) {
      this._options.push(trimmed);
    }
  }

  /**
   * Remove an option by index.
   */
  removeOption(index: number): void {
    if (index >= 0 && index < this._options.length) {
      this._options.splice(index, 1);
    }
  }

  /**
   * Returns the local angle of the pointer relative to the wheel.
   * If the wheel is rotated clockwise by `wheelAngle` (in degrees),
   * the local angle at the top pointer (270 degrees world) is:
   * (270 - wheelAngle) mod 360.
   */
  getLocalPointerAngle(wheelAngle: number): number {
    let localAngle = (270 - wheelAngle) % 360;
    if (localAngle < 0) {
      localAngle += 360;
    }
    return localAngle;
  }

  /**
   * Returns the index of the option that is currently under the pointer
   * at the given wheel rotation angle.
   */
  getOptionIndexAtAngle(wheelAngle: number): number {
    const N = this._options.length;
    if (N === 0) return -1;

    const localAngle = this.getLocalPointerAngle(wheelAngle);
    const segmentAngle = 360 / N;
    const index = Math.floor(localAngle / segmentAngle);

    // Safety clamp in case of floating-point inaccuracies
    return Math.min(Math.max(0, index), N - 1);
  }

  /**
   * Calculates the exact final angle and duration for the wheel to slow down and stop
   * on the specified target option.
   * Uses cubic ease-out deceleration: speed(t) = w0 * (1 - t/T)^2
   * Distance delta = w0 * T / 3
   * Therefore, actual duration T = 3 * delta / w0
   */
  calculateStoppingAngle(
    currentAngle: number,
    speed: number,
    targetIndex: number,
    targetDuration: number = 2.0,
  ): { finalAngle: number; duration: number } {
    const N = this._options.length;
    if (N === 0 || targetIndex < 0 || targetIndex >= N) {
      return { finalAngle: currentAngle, duration: 0 };
    }

    if (speed <= 0.01) {
      return { finalAngle: currentAngle, duration: 0 };
    }

    // 1. Calculate local center angle of target segment
    const segmentAngle = 360 / N;
    const targetCenterLocal = (targetIndex + 0.5) * segmentAngle;

    // 2. Align local target angle to world 270 (top pointer)
    // targetCenterLocal + finalAngle = 270 (mod 360)
    let baseFinalAngle = (270 - targetCenterLocal) % 360;
    if (baseFinalAngle < 0) {
      baseFinalAngle += 360;
    }

    // 3. Find approximate final angle based on desired target duration
    const approxDelta = (speed * targetDuration) / 3;
    const approxFinalAngle = currentAngle + approxDelta;

    // 4. Ensure a minimum rotation (e.g. at least half a spin, or matching a minimum 0.9s duration)
    const minDelta = Math.max(180, speed * 0.3);
    const minFinalAngle = currentAngle + minDelta;

    // 5. Find closest k * 360 matching baseFinalAngle
    const kMin = Math.ceil((minFinalAngle - baseFinalAngle) / 360);
    const kApprox = Math.round((approxFinalAngle - baseFinalAngle) / 360);
    const k = Math.max(kMin, kApprox);

    const finalAngle = baseFinalAngle + k * 360;

    // 6. Calculate exact deceleration duration (in seconds)
    const actualDelta = finalAngle - currentAngle;
    const duration = (3 * actualDelta) / speed;

    return { finalAngle, duration };
  }
}
